import { PageState, InputBarState, InputBarStateConfig, PageStateTransition, RegionVisibilityMapping } from '@/types/VideoPageStateMachine';

// InputBar状态映射表
export const INPUT_BAR_STATES: Record<InputBarState, InputBarStateConfig> = {
  [InputBarState.COMPOSE]: {
    state: InputBarState.COMPOSE,
    ui: {
      containerWidth: 'full',      // 34%
      containerHeight: 'tall',     // 高
      paddingSize: 'large',        // pb-4
      textareaHeight: 100,          // 100px
      textareaDisabled: false,
      showScrollbar: false,
      buttonIcon: 'send',
      buttonDisabled: false,
      buttonVisible: true,
      configVisible: true,
      configDisabled: false
    }
  },
  [InputBarState.PROCESSING]: {
    state: InputBarState.PROCESSING,
    ui: {
      containerWidth: 'compact',   // 24%
      containerHeight: 'short',    // 矮
      paddingSize: 'small',       // pb-2
      textareaHeight: 48,          // 48px
      textareaDisabled: true,      // 禁用输入
      showScrollbar: true,
      buttonIcon: 'stop',         // 停止按钮
      buttonDisabled: false,
      buttonVisible: true,
      configVisible: false,       // 隐藏配置
      configDisabled: true
    }
  },
  [InputBarState.DISABLED]: {
    state: InputBarState.DISABLED,
    ui: {
      containerWidth: 'compact',
      containerHeight: 'short',
      paddingSize: 'small',
      textareaHeight: 48,
      textareaDisabled: true,
      showScrollbar: true,
      buttonIcon: 'spinner',      // 加载图标
      buttonDisabled: true,
      buttonVisible: true,
      configVisible: false,
      configDisabled: true
    }
  },
  [InputBarState.EDITING]: {
    state: InputBarState.EDITING,
    ui: {
      containerWidth: 'compact',   // 24%
      containerHeight: 'short',    // 矮
      paddingSize: 'small',       // pb-2
      textareaHeight: 48,          // 48px
      textareaDisabled: true,      // 禁用输入
      showScrollbar: true,
      buttonIcon: 'stop',         // 停止按钮
      buttonDisabled: false,
      buttonVisible: true,
      configVisible: false,       // 隐藏配置
      configDisabled: true
    }
  }
};

// 页面状态转换规则
export const PAGE_STATE_TRANSITIONS: Record<PageState, PageStateTransition> = {
  [PageState.INITIAL]: {
    inputBarState: InputBarState.COMPOSE,
    allowedNextStates: [PageState.GENERATING, PageState.ERROR],
    regions: {
      tutorial: 'visible',
      sceneList: 'hidden',
      resultArea: 'hidden'
    },
    triggers: {
      enter: '页面加载或重置',
      exit: '用户点击发送按钮'
    }
  },
  [PageState.GENERATING]: {
    inputBarState: InputBarState.PROCESSING,
    allowedNextStates: [PageState.EDITING, PageState.ERROR, PageState.INITIAL],
    regions: {
      tutorial: 'hidden',
      sceneList: 'loading',     // 显示loading状态
      resultArea: 'hidden'
    },
    triggers: {
      enter: '开始生成场景',
      exit: '生成完成/失败/用户停止'
    }
  },
  [PageState.EDITING]: {
    inputBarState: InputBarState.EDITING,
    allowedNextStates: [PageState.GENERATING, PageState.INITIAL],
    regions: {
      tutorial: 'hidden',
      sceneList: 'visible',
      resultArea: 'visible'     // 显示预览区域
    },
    triggers: {
      enter: '生成完成，开始编辑',
      exit: '重新生成或重置'
    }
  },
  [PageState.ERROR]: {
    inputBarState: InputBarState.DISABLED,
    allowedNextStates: [PageState.INITIAL, PageState.GENERATING],
    regions: {
      tutorial: 'hidden',
      sceneList: 'visible',     // 显示错误场景
      resultArea: 'hidden'
    },
    triggers: {
      enter: '生成过程出错',
      exit: '用户重置或重试'
    }
  }
};

// 区域显示映射
export const REGION_VISIBILITY: Record<PageState, RegionVisibilityMapping> = {
  [PageState.INITIAL]: {
    tutorialArea: {
      display: 'block',
      opacity: 1,
      pointerEvents: 'auto'
    },
    sceneListArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    },
    resultArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    }
  },
  [PageState.GENERATING]: {
    tutorialArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    },
    sceneListArea: {
      display: 'block',
      opacity: 1,
      pointerEvents: 'auto',
      overflow: 'visible'
    },
    resultArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    }
  },
  [PageState.EDITING]: {
    tutorialArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    },
    sceneListArea: {
      display: 'block',
      opacity: 1,
      pointerEvents: 'auto'
    },
    resultArea: {
      display: 'block',
      opacity: 1,
      pointerEvents: 'auto'
    }
  },
  [PageState.ERROR]: {
    tutorialArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    },
    sceneListArea: {
      display: 'block',
      opacity: 1,
      pointerEvents: 'auto'
    },
    resultArea: {
      display: 'none',
      opacity: 0,
      pointerEvents: 'none'
    }
  }
};

// 样式映射函数
export const getRegionStyles = (pageState: PageState, region: 'tutorial' | 'sceneList' | 'resultArea') => {
  const visibility = REGION_VISIBILITY[pageState];
  let config;

  switch (region) {
    case 'tutorial':
      config = visibility.tutorialArea;
      break;
    case 'sceneList':
      config = visibility.sceneListArea;
      break;
    case 'resultArea':
      config = visibility.resultArea;
      break;
  }

  return {
    display: config.display,
    opacity: config.opacity,
    pointerEvents: config.pointerEvents,
    overflow: config.overflow || 'visible'
  };
};

// CSS类名映射函数
export const getRegionCSSClasses = (pageState: PageState, region: 'tutorial' | 'sceneList' | 'resultArea') => {
  const visibility = REGION_VISIBILITY[pageState];
  let config;

  switch (region) {
    case 'tutorial':
      config = visibility.tutorialArea;
      break;
    case 'sceneList':
      config = visibility.sceneListArea;
      break;
    case 'resultArea':
      config = visibility.resultArea;
      break;
  }

  const classes = [];

  if (config.display === 'none') {
    classes.push('hidden');
  } else {
    classes.push('flex');
  }

  if (config.opacity === 0) {
    classes.push('opacity-0');
  } else {
    classes.push('opacity-100');
  }

  if (config.pointerEvents === 'none') {
    classes.push('pointer-events-none');
  }

  return classes.join(' ');
};